import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles, Button, IconButton, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Select, MenuItem, InputLabel,
  FormControl, Tooltip, Typography, InputAdornment, LinearProgress, Menu,
} from "@material-ui/core";
import {
  Add, Edit, Delete, Search, GetApp, Publish, ArrowDropDown,
} from "@material-ui/icons";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2), flexWrap: "wrap", alignItems: "center" },
  paper: { overflowX: "auto" },
  table: { minWidth: 700 },
  spacer: { flex: 1 },
}));

const STATES = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const CSV_HEADERS = ["tipo", "nome", "empresa", "documento", "email", "telefone", "endereco", "cidade", "estado", "cep", "observacoes"];
const CSV_TEMPLATE = CSV_HEADERS.join(";") + "\n" +
  "PJ;Empresa Exemplo LTDA;Nome Fantasia;12.345.678/0001-99;contato@empresa.com;(11) 99999-9999;Rua Exemplo 123;São Paulo;SP;01310-100;Observações\n" +
  "PF;João da Silva;;123.456.789-00;joao@email.com;(11) 98888-8888;;;SP;;";

const exportXLSX = (rows, headers, filename) => {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const colWidths = headers.map((h, i) => ({
    wch: Math.max(h.length, ...rows.map((r) => String(r[i] ?? "").length), 10),
  }));
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Dados");
  XLSX.writeFile(wb, filename);
};

const empty = { type: "PJ", name: "", document: "", email: "", phone: "", company: "", address: "", city: "", state: "", zipCode: "", notes: "" };

const parseCSV = (text) => {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(";").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cols = line.split(";");
    const row = {};
    headers.forEach((h, i) => { row[h] = (cols[i] || "").trim(); });
    return row;
  });
};

export default function ProposalClients() {
  const classes = useStyles();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/proposal-clients", { params: { searchParam: search } });
      setClients(data);
    } catch { toast.error("Erro ao carregar clientes"); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleOpen = (client = null) => {
    setEditing(client);
    setForm(client ? { ...client } : empty);
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/proposal-clients/${editing.id}`, form);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/proposal-clients", form);
        toast.success("Cliente criado");
      }
      setOpen(false);
      load();
    } catch { toast.error("Erro ao salvar cliente"); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/proposal-clients/${deleteModal.id}`);
      toast.success("Cliente deletado");
      setDeleteModal({ open: false, id: null, name: "" });
      load();
    } catch { toast.error("Erro ao deletar cliente"); }
  };

  const handleExport = () => {
    setMenuAnchor(null);
    if (clients.length === 0) { toast.info("Nenhum cliente para exportar"); return; }
    const headers = ["Tipo", "Nome / Razão Social", "Empresa", "Documento", "Email", "Telefone", "Endereço", "Cidade", "UF", "CEP", "Observações"];
    const rows = clients.map((c) => [
      c.type || "PJ",
      c.name || "",
      c.company || "",
      c.document || "",
      c.email || "",
      c.phone || "",
      c.address || "",
      c.city || "",
      c.state || "",
      c.zipCode || "",
      c.notes || "",
    ]);
    exportXLSX(rows, headers, "clientes.xlsx");
    toast.success(`${clients.length} cliente(s) exportado(s)`);
  };

  const handleDownloadTemplate = () => {
    setMenuAnchor(null);
    const headers = ["tipo", "nome", "empresa", "documento", "email", "telefone", "endereco", "cidade", "estado", "cep", "observacoes"];
    const rows = [
      ["PJ", "Empresa Exemplo LTDA", "Nome Fantasia", "12.345.678/0001-99", "contato@empresa.com", "(11) 99999-9999", "Rua Exemplo 123", "São Paulo", "SP", "01310-100", "Observações"],
      ["PF", "João da Silva", "", "123.456.789-00", "joao@email.com", "(11) 98888-8888", "", "", "SP", "", ""],
    ];
    exportXLSX(rows, headers, "modelo_clientes.xlsx");
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length === 0) { toast.error("Arquivo vazio ou formato inválido"); return; }

    setImporting(true);
    setImportProgress(0);
    let ok = 0;
    let fail = 0;

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const nome = r["nome"] || r["name"] || "";
      if (!nome) { fail++; continue; }
      const typeRaw = (r["tipo"] || r["type"] || "PJ").toUpperCase();
      const type = typeRaw === "PF" ? "PF" : "PJ";
      const stateRaw = (r["estado"] || r["state"] || "").toUpperCase();
      const state = STATES.includes(stateRaw) ? stateRaw : "";
      try {
        await api.post("/proposal-clients", {
          type,
          name: nome,
          company: r["empresa"] || r["company"] || "",
          document: r["documento"] || r["document"] || "",
          email: r["email"] || "",
          phone: r["telefone"] || r["phone"] || "",
          address: r["endereco"] || r["address"] || "",
          city: r["cidade"] || r["city"] || "",
          state,
          zipCode: r["cep"] || r["zipcode"] || r["zipCode"] || "",
          notes: r["observacoes"] || r["notes"] || "",
        });
        ok++;
      } catch { fail++; }
      setImportProgress(Math.round(((i + 1) / rows.length) * 100));
    }

    setImporting(false);
    setImportProgress(0);
    load();
    if (fail === 0) toast.success(`${ok} cliente(s) importado(s) com sucesso`);
    else toast.warning(`${ok} importado(s), ${fail} com erro`);
  };

  const f = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>Clientes</Typography>

      {importing && (
        <div style={{ marginBottom: 12 }}>
          <Typography variant="caption">Importando... {importProgress}%</Typography>
          <LinearProgress variant="determinate" value={importProgress} />
        </div>
      )}

      <div className={classes.toolbar}>
        <TextField
          size="small" variant="outlined" placeholder="Buscar nome, CNPJ, email..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          style={{ minWidth: 280 }}
        />
        <div className={classes.spacer} />

        {/* Import button */}
        <input
          ref={fileInputRef} type="file" accept=".csv"
          style={{ display: "none" }} onChange={handleImportFile}
        />
        <Button
          variant="outlined" startIcon={<Publish />}
          onClick={() => fileInputRef.current.click()}
          disabled={importing}
        >
          Importar CSV
        </Button>

        {/* Export menu */}
        <Button
          variant="outlined" startIcon={<GetApp />} endIcon={<ArrowDropDown />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          Exportar
        </Button>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={handleExport}>
            <GetApp fontSize="small" style={{ marginRight: 8 }} /> Exportar lista atual (.xlsx)
          </MenuItem>
          <MenuItem onClick={handleDownloadTemplate}>
            <GetApp fontSize="small" style={{ marginRight: 8 }} /> Baixar modelo de importação (.xlsx)
          </MenuItem>
        </Menu>

        <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
          Novo Cliente
        </Button>
      </div>

      <Paper className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Nome / Razão Social</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Cidade/UF</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.type}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.company || "—"}</TableCell>
                <TableCell>{c.document || "—"}</TableCell>
                <TableCell>{c.email || "—"}</TableCell>
                <TableCell>{c.phone || "—"}</TableCell>
                <TableCell>{c.city && c.state ? `${c.city}/${c.state}` : c.city || c.state || "—"}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpen(c)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => setDeleteModal({ open: true, id: c.id, name: c.name })}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {clients.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">Nenhum cliente encontrado</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Tipo *</InputLabel>
                <Select label="Tipo *" value={form.type} onChange={f("type")}>
                  <MenuItem value="PF">Pessoa Física</MenuItem>
                  <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={9}>
              <TextField fullWidth label="Nome / Razão Social *" value={form.name} onChange={f("name")} variant="outlined" size="small" />
            </Grid>
            {form.type === "PJ" && (
              <Grid item xs={6}>
                <TextField fullWidth label="Nome Fantasia / Empresa" value={form.company || ""} onChange={f("company")} variant="outlined" size="small" />
              </Grid>
            )}
            <Grid item xs={form.type === "PJ" ? 6 : 12}>
              <TextField fullWidth label={form.type === "PF" ? "CPF" : "CNPJ"} value={form.document || ""} onChange={f("document")} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Email" value={form.email || ""} onChange={f("email")} variant="outlined" size="small" type="email" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Telefone / WhatsApp" value={form.phone || ""} onChange={f("phone")} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth label="Endereço" value={form.address || ""} onChange={f("address")} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="CEP" value={form.zipCode || ""} onChange={f("zipCode")} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth label="Cidade" value={form.city || ""} onChange={f("city")} variant="outlined" size="small" />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>UF</InputLabel>
                <Select label="UF" value={form.state || ""} onChange={f("state")}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observações" value={form.notes || ""} onChange={f("notes")} variant="outlined" size="small" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={!form.name}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir Cliente"
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: "" })}
        onConfirm={handleDelete}
      >
        Deseja excluir o cliente <strong>{deleteModal.name}</strong>?
      </ConfirmationModal>
    </div>
  );
}
